import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Plus, X } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DeviceStreamCard } from "@/components/device/DeviceStreamCard";
import { DeviceFullscreenOverlay } from "@/components/device/DeviceFullscreenOverlay";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useRealtime } from "@/hooks/useRealtime";
import { toast } from "sonner";
import dayjs from "dayjs";

export default function ControlRoomPage() {
  const [cameras, setCameras] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [fullscreenCamera, setFullscreenCamera] = useState(null);
  const { lastEvent } = useRealtime(true);

  const load = async () => {
    setLoading(true);
    try {
      const [cameraRes, pendingRes] = await Promise.all([
        api.get("/cameras"),
        api.get("/enrollment-requests"),
      ]);
      setCameras(cameraRes.data);
      setPending(pendingRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (lastEvent && ["device.online", "device.offline", "camera.online", "camera.offline"].includes(lastEvent.type)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEvent]);

  const pollMs = cameras.length >= 5 ? 300 : cameras.length >= 3 ? 200 : 150;

  const decide = async (deviceId, action) => {
    setBusyId(deviceId);
    try {
      await api.post(`/enrollment-requests/${deviceId}/${action}`);
      toast.success(action === "approve" ? "Demande approuvée" : "Demande refusée");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const gridClass = (() => {
    if (cameras.length <= 1) return "grid-cols-1 max-w-5xl";
    if (cameras.length <= 4) return "grid-cols-1 md:grid-cols-2";
    if (cameras.length <= 6) return "grid-cols-1 md:grid-cols-2 xl:grid-cols-3";
    return "grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4";
  })();

  return (
    <div data-testid="control-room-page">
      <PageHeader
        title="Mes caméras"
        description={cameras.length > 0 ? `${cameras.length} caméra${cameras.length > 1 ? "s" : ""}` : undefined}
        actions={
          <Link to="/cameras/new" data-testid="control-add-camera" className="inline-flex h-11 items-center gap-2 rounded-xl bg-brand px-3.5 text-sm font-medium text-white transition-colors hover:bg-brand-hover">
            <Plus className="size-4" strokeWidth={1.8} />
            <span className="hidden sm:inline">Ajouter une caméra</span>
            <span className="sm:hidden">Ajouter</span>
          </Link>
        }
      />

      <div className="space-y-8 px-4 pb-10 sm:px-6 lg:px-8">
        <section>
          {loading ? (
            <div className="animate-pulse py-12 text-sm text-zinc-500" data-testid="control-loading">Chargement…</div>
          ) : cameras.length === 0 ? (
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-8 text-center sm:p-12" data-testid="control-empty">
              <h2 className="text-lg font-semibold text-white">Aucune caméra pour le moment</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-zinc-400">Ajoutez votre première caméra pour commencer à afficher vos flux vidéo dans CAM.</p>
              <Link to="/cameras/new" data-testid="control-empty-add-camera" className="mt-5 inline-flex h-11 items-center gap-2 rounded-xl bg-brand px-4 text-sm font-medium text-white transition-colors hover:bg-brand-hover">
                <Plus className="size-4" strokeWidth={1.8} /> Ajouter une caméra
              </Link>
            </div>
          ) : (
            <div className={`grid gap-4 ${gridClass}`} data-testid="control-grid">
              {cameras.map((camera) => (
                <DeviceStreamCard key={camera.id} camera={camera} pollMs={pollMs} onFullscreen={setFullscreenCamera} />
              ))}
            </div>
          )}
        </section>

        {pending.length > 0 && (
          <section className="pt-2" data-testid="pending-section">
            <div className="mb-3 flex items-center gap-3">
              <div className="text-sm font-medium text-zinc-300">Demandes d’appareil</div>
              <div className="text-xs text-zinc-500">{pending.length} en attente</div>
            </div>
            <div className="divide-y divide-white/[0.06] rounded-2xl border border-white/[0.08] bg-white/[0.025]" data-testid="pending-list">
              {pending.map((p) => (
                <div key={p.id} className="flex flex-col justify-between gap-3 px-4 py-3 sm:flex-row sm:items-center" data-testid={`pending-row-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <span className="truncate text-sm font-semibold" data-testid={`pending-name-${p.id}`}>{p.name}</span>
                    <div className="mt-0.5 text-xs text-zinc-500">{p.type} · {p.os} · {dayjs(p.created_at).format("DD/MM HH:mm")}</div>
                  </div>
                  <div className="flex gap-2 sm:shrink-0">
                    <Button size="sm" onClick={() => decide(p.id, "approve")} disabled={busyId === p.id} className="h-11 flex-1 rounded-lg bg-status-online text-white hover:bg-status-online/80 sm:flex-none" data-testid={`pending-approve-${p.id}`}>
                      <Check className="mr-1 size-3.5" strokeWidth={2} /> Approuver
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decide(p.id, "reject")} disabled={busyId === p.id} className="h-11 flex-1 rounded-lg border-status-offline/30 bg-transparent text-red-300 hover:bg-status-offline/10 sm:flex-none" data-testid={`pending-reject-${p.id}`}>
                      <X className="mr-1 size-3.5" strokeWidth={2} /> Refuser
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {fullscreenCamera && (
        <DeviceFullscreenOverlay
          camera={cameras.find((item) => item.id === fullscreenCamera.id) || fullscreenCamera}
          cameras={cameras}
          onCameraChange={setFullscreenCamera}
          onClose={() => setFullscreenCamera(null)}
        />
      )}
    </div>
  );
}
